import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { Circle, Triangle } from "@remotion/shapes";
import { noise2D } from "@remotion/noise";
import { SafeZoneOverlay } from "./components/elements/SafeZoneOverlay";

export const ThirdPartyElementsShowcase: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 1. Organic Noise Camera Drift (@remotion/noise)
  const camNoiseX = noise2D("camX", frame / 35, 0) * 8;
  const camNoiseY = noise2D("camY", 0, frame / 35) * 6;

  // 2. Spring Animations
  const titleSpring = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 140 },
  });

  const card1Spring = spring({
    frame: frame - 10,
    fps,
    config: { damping: 14, stiffness: 150 },
  });

  const card2Spring = spring({
    frame: frame - 20,
    fps,
    config: { damping: 14, stiffness: 150 },
  });

  const card3Spring = spring({
    frame: frame - 30,
    fps,
    config: { damping: 14, stiffness: 150 },
  });

  // Shapes rotation & scale
  const shapeRotation = interpolate(frame, [0, 300], [0, 360]);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#070a08",
        color: "#ffffff",
        fontFamily: "'Inter', -apple-system, sans-serif",
        boxSizing: "border-box",
        transform: `translate(${camNoiseX}px, ${camNoiseY}px)`,
      }}
    >
      {/* Visual Safe Zone Guide */}
      <SafeZoneOverlay visible={true} opacity={0.35} />

      {/* Background Animated Vector Shapes (@remotion/shapes) */}
      <div
        style={{
          position: "absolute",
          top: "150px",
          right: "80px",
          opacity: 0.15,
          transform: `rotate(${shapeRotation}deg)`,
          pointerEvents: "none",
        }}
      >
        <Triangle length={240} direction="up" fill="none" stroke="#e8f36e" strokeWidth={4} />
      </div>

      <div
        style={{
          position: "absolute",
          bottom: "350px",
          left: "40px",
          opacity: 0.12,
          pointerEvents: "none",
        }}
      >
        <Circle radius={140} fill="none" stroke="#ffffff" strokeWidth={3} />
      </div>

      {/* Main Safe Box Container */}
      <div
        style={{
          position: "absolute",
          top: "220px",
          left: "80px",
          right: "140px",
          bottom: "440px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          zIndex: 10,
        }}
      >
        {/* HEADER */}
        <div
          style={{
            transform: `translateY(${interpolate(titleSpring, [0, 1], [-20, 0])}px)`,
            opacity: titleSpring,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              backgroundColor: "rgba(232,243,110,0.1)",
              border: "1px solid #e8f36e",
              padding: "8px 18px",
              borderRadius: "100px",
              marginBottom: "18px",
            }}
          >
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#e8f36e",
                boxShadow: "0 0 10px #e8f36e",
              }}
            />
            <div
              style={{
                fontSize: "14px",
                fontWeight: 800,
                color: "#e8f36e",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              3RD-PARTY REMOTION ECOSYSTEM
            </div>
          </div>

          <div
            style={{
              fontSize: "64px",
              fontWeight: 900,
              lineHeight: 1.05,
              letterSpacing: "-0.04em",
            }}
          >
            Community & 3rd Party
          </div>
          <div
            style={{
              fontSize: "64px",
              fontWeight: 900,
              lineHeight: 1.05,
              color: "#e8f36e",
              letterSpacing: "-0.04em",
            }}
          >
            Element Libraries.
          </div>
          <div
            style={{
              fontSize: "20px",
              color: "#84968d",
              marginTop: "10px",
              fontWeight: 500,
            }}
          >
            Library resmi dari ekosistem remotion.dev yang siap diintegrasikan.
          </div>
        </div>

        {/* 3RD PARTY PACKAGES SHOWCASE */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Card 1: Remocn (Shadcn for Remotion) */}
          <div
            style={{
              backgroundColor: "rgba(20, 26, 23, 0.85)",
              border: "1px solid rgba(232,243,110,0.3)",
              borderRadius: "18px",
              padding: "24px 28px",
              transform: `translateY(${interpolate(card1Spring, [0, 1], [30, 0])}px)`,
              opacity: card1Spring,
              backdropFilter: "blur(16px)",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                fontFamily: "monospace",
                color: "#e8f36e",
                fontWeight: 700,
              }}
            >
              01 // REMOCN (remocn.dev)
            </div>
            <div style={{ fontSize: "32px", fontWeight: 800, color: "#ffffff", marginTop: "4px" }}>
              Shadcn UI for Remotion
            </div>
            <div style={{ fontSize: "16px", color: "#97a89f", marginTop: "4px" }}>
              252+ komponen video: Animated Charts, Kinetic Captions, Split Flaps, dan UI blocks.
            </div>
          </div>

          {/* Card 2: Remotion Bits (remotion-bits.dev) */}
          <div
            style={{
              backgroundColor: "rgba(20, 26, 23, 0.85)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "18px",
              padding: "24px 28px",
              transform: `translateY(${interpolate(card2Spring, [0, 1], [30, 0])}px)`,
              opacity: card2Spring,
              backdropFilter: "blur(16px)",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                fontFamily: "monospace",
                color: "#ffffff",
                fontWeight: 700,
              }}
            >
              02 // REMOTION BITS (remotion-bits.dev)
            </div>
            <div style={{ fontSize: "32px", fontWeight: 800, color: "#ffffff", marginTop: "4px" }}>
              Animated Motion Blocks
            </div>
            <div style={{ fontSize: "16px", color: "#97a89f", marginTop: "4px" }}>
              TypeWriter, MatrixRain, 3D Steps, ParticleSystem, dan Staggered Transitions.
            </div>
          </div>

          {/* Card 3: @remotion/lottie & @remotion/shapes */}
          <div
            style={{
              backgroundColor: "rgba(20, 26, 23, 0.85)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "18px",
              padding: "24px 28px",
              transform: `translateY(${interpolate(card3Spring, [0, 1], [30, 0])}px)`,
              opacity: card3Spring,
              backdropFilter: "blur(16px)",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                fontFamily: "monospace",
                color: "#e8f36e",
                fontWeight: 700,
              }}
            >
              03 // CORE PLUGINS (@remotion/lottie & shapes)
            </div>
            <div style={{ fontSize: "32px", fontWeight: 800, color: "#ffffff", marginTop: "4px" }}>
              After Effects & Vector Shapes
            </div>
            <div style={{ fontSize: "16px", color: "#97a89f", marginTop: "4px" }}>
              Render animasi Lottie After Effects, SVG shapes dinamis, dan Camera Noise.
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.1)",
            paddingTop: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: "16px", color: "#84968d", fontWeight: 500 }}>
            Tersedia langsung di project studio lokal
          </div>
          <div style={{ fontSize: "18px", fontWeight: 800, color: "#e8f36e" }}>
            STUDIO.KYUIDN.CLOUD ➔
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
